import React, {useEffect, useMemo} from 'react';
import {useTranslation} from 'react-i18next';
import {useDispatch} from 'react-redux';
import BorderRadiusChange from '../../../../components/slides/highlighted/common/borderRadiusChange';
import FontAndBackgroundColorPicker, {COLOR_FIELDS} from '../../../../components/slides/highlighted/common/fontAndBackgroundColorPicker';
import SectionTitle from '../../../../components/elements/common/SectionTitle';
import Tiptap from '../../../../components/elements/tipTap/TipTap';
import {useGetEditor} from '../../../../components/elements/tipTap/getEditor';
import {IBorderRadius} from '../../../../incase-domain/common/borderRadius';
import {ELEMENT} from '../../../../incase-domain/elements/base/ElementTypes';
import {IElementStaticText} from '../../../../incase-domain/elements/staticText';
import {FONT_SIZE, IPresentationTheme} from '../../../../incase-domain/presentationTheme';
import {setEditorMode} from '../../../../store/actions/tiptapEditor.actions';
import {EditorModeEnum} from '../../../../store/actions/types/tiptapEditor.types';
import {defaultFonts} from '../../../../utils/defaultFonts';
import {THEME_TEXT_PREVIEW_ID} from './ThemePreviewPanel';

interface PresentationThemeTextElementProps {
    presentationTheme: IPresentationTheme;
    setPresentationTheme: React.Dispatch<React.SetStateAction<IPresentationTheme>>;
}

function PresentationThemeTextElement(props: PresentationThemeTextElementProps): JSX.Element {
    const {presentationTheme, setPresentationTheme} = props;
    const {t} = useTranslation();
    const dispatch = useDispatch();

    const textTheme = presentationTheme.elementStyles?.textElement;
    const selectedFont =
        [...(presentationTheme.fonts || []), ...(presentationTheme.defaultFonts || defaultFonts)].find((font) => font.selected) ||
        defaultFonts[0];

    const mockElement = useMemo((): IElementStaticText => {
        return {
            id: THEME_TEXT_PREVIEW_ID,
            name: 'theme-text-preview',
            type: ELEMENT.STATIC_TEXT,
            xPosition: 0,
            yPosition: 0,
            angle: 0,
            height: 'auto',
            width: '100%',
            slideId: 'theme-preview',
            keyIndex: THEME_TEXT_PREVIEW_ID,
            elementIndex: 0,
            title: '',
            label: '',
            text: `<p style="font-size: ${textTheme?.fontSize?.value || 1.5}em; color: ${textTheme?.color || '#000000'}">${t(
                'presentationThemesPage.text.textElement'
            )}</p>`,
            color: textTheme?.backgroundColor || '',
            borderRadius: textTheme?.borderRadius,
            font: {
                color: textTheme?.color || '#000000',
                size: textTheme?.fontSize?.value || 1.5,
            },
            fontFamily: selectedFont,
            slideChange: {slideId: '', name: ''},
        };
    }, [textTheme?.backgroundColor, textTheme?.borderRadius, textTheme?.color, textTheme?.fontSize?.value, selectedFont, t]);

    const changeHandler = {
        single: (field: string, value: unknown): void => {
            setPresentationTheme((prevTheme) => {
                const prevText = prevTheme.elementStyles?.textElement || {};

                if (field === 'color') {
                    return {
                        ...prevTheme,
                        elementStyles: {
                            ...prevTheme.elementStyles,
                            textElement: {...prevText, backgroundColor: value as string},
                        },
                    };
                }

                if (field === 'borderRadius') {
                    return {
                        ...prevTheme,
                        elementStyles: {
                            ...prevTheme.elementStyles,
                            textElement: {...prevText, borderRadius: value as IBorderRadius},
                        },
                    };
                }

                if (field === 'font' && value && typeof value === 'object' && 'color' in value) {
                    return {
                        ...prevTheme,
                        elementStyles: {
                            ...prevTheme.elementStyles,
                            textElement: {...prevText, color: (value as {color: string}).color},
                        },
                    };
                }

                return prevTheme;
            });
        },
    };

    const {editor} = useGetEditor({id: THEME_TEXT_PREVIEW_ID, element: mockElement});

    useEffect(() => {
        dispatch(setEditorMode(EditorModeEnum.SELECT));
        return () => {
            dispatch(setEditorMode(null));
        };
    }, [dispatch]);

    useEffect(() => {
        if (!editor) return undefined;

        const syncThemeFromEditor = (): void => {
            const color = editor.getAttributes('textStyle').color as string | undefined;
            const fontSizeAttr = editor.getAttributes('textStyle').fontSize as string | undefined;
            const fontSizeValue = fontSizeAttr?.includes('em') ? parseFloat(fontSizeAttr) : undefined;

            setPresentationTheme((prevTheme) => {
                const prevText = prevTheme.elementStyles?.textElement || {};
                let nextFontSize = prevText.fontSize;

                if (fontSizeValue && !Number.isNaN(fontSizeValue)) {
                    const matched = prevTheme.fontSizes.find((size) => Math.abs(size.value - fontSizeValue) < 0.001);
                    nextFontSize = matched || {size: FONT_SIZE.MEDIUM, value: fontSizeValue};
                }

                if (prevText.color === (color || prevText.color) && prevText.fontSize?.value === nextFontSize?.value) {
                    return prevTheme;
                }

                return {
                    ...prevTheme,
                    elementStyles: {
                        ...prevTheme.elementStyles,
                        textElement: {
                            ...prevText,
                            ...(color ? {color} : {}),
                            ...(nextFontSize ? {fontSize: nextFontSize} : {}),
                        },
                    },
                };
            });
        };

        editor.on('transaction', syncThemeFromEditor);
        return () => {
            editor.off('transaction', syncThemeFromEditor);
        };
    }, [editor, setPresentationTheme]);

    return (
        <div className="l-group--form">
            <SectionTitle label={t('designSidepanel.style')} />
            <div className="u-flex u-flex-col u-gap-3">
                <Tiptap id={THEME_TEXT_PREVIEW_ID} />

                <FontAndBackgroundColorPicker
                    highlightedElement={mockElement}
                    highlightedElementChangeHandler={changeHandler}
                    colorFields={[COLOR_FIELDS.BACKGROUND_COLOR]}
                />

                <BorderRadiusChange
                    borderRadius={mockElement.borderRadius}
                    highlightedElementChangeHandler={(value) => changeHandler.single('borderRadius', value)}
                />
            </div>
        </div>
    );
}

export default PresentationThemeTextElement;
